import React from "react";
import { Box, Typography, IconButton, TextField } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast } from "react-toastify";
import { COLORS } from "../../assets/theme";
import CustomTable from "../../components/table/CustomTable";
import SearchHeaderContainer from "../../components/table/SearchHeaderContainer";
import Actions from "../../components/Actions";
import GenericFormDialog, { Field } from "../../components/GenericFormDialog";
import ConfirmDialog from "../../components/ConfirmDialog";
import PageHeader from "../../components/PageHeader";
import CustomDialog from "../../components/CustomDialog";
import CustomButton from "../../components/CustomButton";
import CustomCheckbox from "../../components/CustomCheckbox";
import { useLocation, useParams } from "react-router-dom";
import {
  createCommunity,
  deleteCommunity,
  getAllCommunity,
  updateCommunity,
  AssignClients,
  GetAssignedClients,
  deleteMember,
} from "../../services/CommunityService";
import { getClientsPage } from "../../services/ClientsService";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";

const getErrorMessage = (error: any, fallback: string) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

const showErrorToast = (message: any) =>
  toast.error(String(message), { toastId: String(message) });

const COMMUNITY_CLIENTS_PAGE_SIZE = 30;

const Community: React.FC = () => {
  const params = useParams<{ businessId?: string; id?: string }>();
  const venueId = params.businessId || params.id;

  const [editOpen, setEditOpen] = React.useState(false);
  const [editingRow, setEditingRow] = React.useState<any | null>(null);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [toDeleteId, setToDeleteId] = React.useState<string | number | null>(
    null,
  );

  const [search, setSearch] = React.useState<string>("");
  const [loading, setLoading] = React.useState<boolean>(false);
  const [refetchTrigger, setRefetchTrigger] = React.useState(0);

  const [clients, setClients] = React.useState<any[]>([]);
  const [parentDialogOpen, setParentDialogOpen] = React.useState(false);
  const [selectedClient, setSelectedClient] = React.useState<
    (string | number)[]
  >([]);
  const [currentCommunityId, setCurrentCommunityId] = React.useState<
    string | number | null
  >(null);
  const [assignedClients, setAssignedClients] = React.useState<any[]>([]);
  const [assignedClientIds, setAssignedClientIds] = React.useState<string[]>(
    [],
  );
  const [viewDialogOpen, setViewDialogOpen] = React.useState(false);
  const [clientSearch, setClientSearch] = React.useState("");

  // The picker lists clients a page at a time from the server, searched there.
  const debouncedClientSearch = useDebouncedValue(clientSearch, 300);
  const [clientsTotal, setClientsTotal] = React.useState(0);
  const [clientsPage, setClientsPage] = React.useState(0);
  const [loadingClients, setLoadingClients] = React.useState(false);
  const [selectingAll, setSelectingAll] = React.useState(false);
  const clientsRequestRef = React.useRef(0);
  const filteredClients = clients;
  const hasMoreClients = clients.length < clientsTotal;

  const loadClientsPage = React.useCallback(
    async (page: number, term: string) => {
      const requestId = ++clientsRequestRef.current;
      setLoadingClients(true);
      try {
        const result = await getClientsPage({
          page,
          size: COMMUNITY_CLIENTS_PAGE_SIZE,
          search: term,
        });
        if (requestId !== clientsRequestRef.current) return;
        const content = result?.content ?? [];
        setClients((prev) => (page === 0 ? content : [...prev, ...content]));
        setClientsTotal(result?.totalElements ?? content.length);
        setClientsPage(page);
      } catch (error) {
        if (requestId === clientsRequestRef.current) {
          showErrorToast(getErrorMessage(error, "Failed to fetch clients."));
        }
      } finally {
        if (requestId === clientsRequestRef.current) setLoadingClients(false);
      }
    },
    [],
  );

  React.useEffect(() => {
    if (!parentDialogOpen) return;
    loadClientsPage(0, debouncedClientSearch);
  }, [debouncedClientSearch, loadClientsPage, parentDialogOpen]);

  // "Select All" means every client matching the search, including the ones
  // not loaded into the list yet, so fetch their ids only when asked.
  const selectAllMatchingClients = async () => {
    setSelectingAll(true);
    try {
      const ids: (string | number)[] = [];
      for (let page = 0; ; page += 1) {
        const result = await getClientsPage({
          page,
          size: 100,
          search: debouncedClientSearch,
        });
        const content = result?.content ?? [];
        ids.push(...content.map((client: any) => client.id));
        if (page + 1 >= (result?.totalPages ?? 1) || content.length === 0) {
          break;
        }
      }
      setSelectedClient((prev) =>
        Array.from(new Set([...prev.map(String), ...ids.map(String)])),
      );
    } catch (error) {
      showErrorToast(getErrorMessage(error, "Failed to select clients."));
    } finally {
      setSelectingAll(false);
    }
  };

  const formFields: Field[] = [
    { name: "name", label: "Name" },
    { name: "description", label: "Description" },
  ];

  const openEditForId = (id: string | number) => {
    const found = rows.find((r: any) => String(r.id) === String(id));
    if (found) {
      setEditingRow(found);
      setEditOpen(true);
    }
  };
  const wiredActions = Actions({
    handleOpenEditModal: { handler: openEditForId },
    onDelete: {
      handler: async (id: number | string) => {
        setToDeleteId(id);
        setConfirmOpen(true);
      },
    },
    onView: {
      handler: async (id: number | string) => {
        setCurrentCommunityId(id);
        try {
          const response = await GetAssignedClients(String(id));
          let assignedData = [];
          if (response && response.members) {
            assignedData = response.members;
          } else if (Array.isArray(response)) {
            assignedData = response;
          }
          setAssignedClients(Array.isArray(assignedData) ? assignedData : []);
          setViewDialogOpen(true);
      } catch (error) {
        console.error("Error fetching assigned clients:", error);
        showErrorToast(
          getErrorMessage(error, "Failed to fetch assigned clients.")
        );
      }
      },
    },
    onParentIcon: {
      handler: async (id: string | number) => {
        setCurrentCommunityId(id);
        try {
          setClients([]);
          setClientsTotal(0);
          setClientSearch("");
          try {
            const assignedResp: any = await GetAssignedClients(String(id));
            let assignedData: any[] = [];
            if (assignedResp && assignedResp.members) {
              assignedData = assignedResp.members;
            } else if (Array.isArray(assignedResp)) {
              assignedData = assignedResp;
            }

            const assignedIds = (assignedData || [])
              .map((m: any) => m.organization_user?.id)
              .filter(Boolean)
              .map(String);

            setSelectedClient(assignedIds);
            setAssignedClientIds(assignedIds);
            setAssignedClients(Array.isArray(assignedData) ? assignedData : []);
          } catch (err) {
            console.error(
              "Could not fetch assigned clients for preselect",
              err,
            );
            setSelectedClient([]);
            setAssignedClientIds([]);
          }
          setParentDialogOpen(true);
      } catch (error) {
        console.error("Error fetching clients:", error);
        showErrorToast(getErrorMessage(error, "Failed to fetch clients."));
      }
      },
    },
  });

  const [rows, setRows] = React.useState<any[]>([]);
  const rowsRef = React.useRef<any[]>([]);
  React.useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  const refreshCommunityCounts = React.useCallback(async () => {
    try {
      const currentRows = rowsRef.current || [];
      if (!Array.isArray(currentRows) || currentRows.length === 0) return;

      const counts = await Promise.all(
        currentRows.map(async (r: any) => {
          try {
            const resp = await GetAssignedClients(String(r.id));
            let assigned: any[] = [];
            if (resp && resp.members) assigned = resp.members;
            else if (Array.isArray(resp)) assigned = resp;
            return Array.isArray(assigned) ? assigned.length : 0;
          } catch (err) {
            return 0;
          }
        }),
      );

      setRows((prevRows) =>
        prevRows.map((r: any, i: number) => ({
          ...r,
          members_count: counts[i] ?? r.members_count ?? 0,
        })),
      );
    } catch (err) {
      console.error("Error refreshing community counts", err);
    }
  }, []);

  React.useEffect(() => {
    const intervalMs = 30000;
    const id = setInterval(() => {
      refreshCommunityCounts();
    }, intervalMs);

    refreshCommunityCounts();

    return () => clearInterval(id);
  }, [refreshCommunityCounts]);

  React.useEffect(() => {
    const fetchSpecialities = async () => {
      setLoading(true);
      try {
        const response = await getAllCommunity();
        console.log("Community fetched:", response);

        let specialitiesData: any[] = [];
        if (response && Array.isArray(response)) {
          specialitiesData = response;
        } else if (response && response.data) {
          specialitiesData = Array.isArray(response.data)
            ? response.data
            : response.data.community || [];
        } else if (response && response.community) {
          specialitiesData = response.community;
        } else if (response && response.groups) {
          specialitiesData = response.groups;
        }

        const mapped = (specialitiesData || []).map((s: any) => ({
          id: s.id ?? s._id ?? s.speciality_id,
          name: s.name ?? s.title ?? "",
          description: s.description ?? "",
          members_count: s.members_count ?? 0,
        }));

        setRows(mapped);

        (async () => {
          try {
            const counts = await Promise.all(
              mapped.map(async (row: any) => {
                try {
                  const resp = await GetAssignedClients(String(row.id));
                  let assigned = [];
                  if (resp && resp.members) {
                    assigned = resp.members;
                  } else if (Array.isArray(resp)) {
                    assigned = resp;
                  }
                  return Array.isArray(assigned) ? assigned.length : 0;
                } catch (err) {
                  return 0;
                }
              }),
            );

            const mappedWithCounts = mapped.map((r: any, idx: number) => ({
              ...r,
              members_count: counts[idx] ?? 0,
            }));
            setRows(mappedWithCounts);
          } catch (err) {
            // if fetching counts fails, keep rows with default counts
            console.error("Failed to fetch community member counts:", err);
          }
        })();
      } catch (error: any) {
        console.error("Error fetching community:", error);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSpecialities();
  }, [refetchTrigger, venueId]);

  return (
    <Box sx={{ width: "100%", maxWidth: "90vw", overflowX: "hidden" }}>
      <Box sx={{ mt: { xs: 2, sm: 3 } }}>
        <PageHeader
          title="Community"
          subtitle="Client groups and their members."
        />
        <SearchHeaderContainer
          search={search}
          setSearch={setSearch}
          buttonConfig={[
            {
              size: "small",
              bgColor: COLORS.MainColor,
              startIcon: <AddIcon />,
              onClick: () => {
                setEditingRow(null);
                setEditOpen(true);
              },
              textColor: COLORS.WhiteText,
              minHeight: 36,
              maxHeight: 36,
              sx: {
                width: { xs: "96%", sm: 36 },
                minWidth: { sm: 36 },
                marginRight: { xs: 1.5, sm: 0 },
              },
            },
          ]}
        />
        <CustomTable<any>
          filters={{ page: 0, size: 10, search }}
          clientSidePagination={true}
          loading={loading}
          fields={[
            { key: "name", header: "Name" },
            { key: "members_count" as any, header: "Member Count" },
            { key: "description", header: "Description" },
          ]}
          actions={wiredActions}
          fetchFunc={async (params?: any) => {
            const q = (params && params.search) || search || "";

            const normalizedQuery = String(q || "")
              .toLowerCase()
              .trim();

            const filtered = rows.filter((r: any) => {
              if (!normalizedQuery) return true;

              const haystack = [r.name, r.description]
                .filter(Boolean)
                .map((v: any) => String(v).toLowerCase())
                .join(" ");

              return haystack.includes(normalizedQuery);
            });

            const content = filtered;
            const totalElements = content.length;
            const pageSize = (params && params.size) || 10;
            const totalPages = Math.max(1, Math.ceil(totalElements / pageSize));

            return {
              content,
              number: 0,
              size: pageSize,
              totalElements,
              totalPages,
            };
          }}
        />
        <GenericFormDialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          title={editingRow ? `Edit Community` : "Add Community"}
          fields={editingRow ? formFields : [...formFields]}
          initialValues={
            editingRow
              ? {
                  name: editingRow.name || "",
                  description: editingRow.description || "",
                }
              : {}
          }
          onSubmit={async (values) => {
            try {
              if (editingRow) {
                await updateCommunity(String(editingRow.id), {
                  name: values.name,
                  description: values.description,
                });
                toast.success("Community updated successfully!", {
                  position: "top-right",
                });
                setRefetchTrigger((prev) => prev + 1);
              } else {
                await createCommunity({
                  name: values.name,
                  description: values.description,
                });
                toast.success("Community created successfully!", {
                  position: "top-right",
                });
                setRefetchTrigger((prev) => prev + 1);
              }
              setEditOpen(false);
              setEditingRow(null);
            } catch (error: any) {
              console.error("Error saving community:", error);
              showErrorToast(
                getErrorMessage(
                  error,
                  "Failed to save community. Please try again."
                )
              );
            }
          }}
        />
        <ConfirmDialog
          open={confirmOpen}
          onClose={() => {
            setConfirmOpen(false);
            setToDeleteId(null);
          }}
          title="Delete entry"
          message="Are you sure you want to delete this entry? This action cannot be undone."
          onConfirm={async () => {
            if (toDeleteId === null) return;
            try {
              await deleteCommunity(String(toDeleteId));
              toast.success("Community deleted successfully!", {
                position: "top-right",
              });
              setRefetchTrigger((prev) => prev + 1);
            } catch (error: any) {
              console.error("Error deleting community:", error);
              showErrorToast(
                getErrorMessage(
                  error,
                  "Failed to delete community. Please try again."
                )
              );
            } finally {
              setConfirmOpen(false);
              setToDeleteId(null);
            }
          }}
        />
        <CustomDialog
          open={parentDialogOpen}
          onClose={() => setParentDialogOpen(false)}
          title="Select Clients"
          actions={
            <Box sx={{ display: "flex", gap: 1 }}>
              <CustomButton onClick={() => setParentDialogOpen(false)}>
                Close
              </CustomButton>
              <CustomButton
                onClick={async () => {
                  if (currentCommunityId && selectedClient.length > 0) {
                    try {
                      await AssignClients(String(currentCommunityId), {
                        client_ids: selectedClient.map(String),
                      });

                      toast.success("Clients assigned successfully!", {
                        position: "top-right",
                      });
                      setAssignedClientIds(
                        selectedClient.map((id) => String(id)),
                      );
                      setParentDialogOpen(false);
                      setSelectedClient([]);
                      setCurrentCommunityId(null);
                      // Refresh member counts immediately after assignment
                      refreshCommunityCounts();
                    } catch (error) {
                      console.error("Error assigning clients:", error);
                      showErrorToast(
                        getErrorMessage(
                          error,
                          "Failed to assign clients. Please try again.",
                        )
                      );
                    }
                  } else {
                    toast.error("Please select clients to assign.", {
                      position: "top-right",
                    });
                  }
                }}
              >
                Save
              </CustomButton>
            </Box>
          }
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              maxHeight: 400,
            }}
          >
            <TextField
              size="small"
              label="Search by name or phone"
              value={clientSearch}
              onChange={(e) => setClientSearch(e.target.value)}
            />
            <Box sx={{ maxHeight: 340, overflowY: "auto", pr: 1 }}>
              <CustomCheckbox
                label={
                  selectingAll
                    ? "Selecting all…"
                    : `Select All${clientsTotal ? ` (${clientsTotal})` : ""}`
                }
                checked={
                  clients.length > 0 &&
                  !hasMoreClients &&
                  filteredClients.every((c) =>
                    selectedClient.some((s) => String(s) === String(c.id)),
                  )
                }
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  if (e.target.checked) {
                    selectAllMatchingClients();
                  } else {
                    setSelectedClient([]);
                  }
                }}
              />
              {filteredClients.map((client) => {
                const clientVal = client.id;
                const clientValStr = String(clientVal);
                const name =
                  client.full_name ||
                  client.name ||
                  client.fullName ||
                  "Unknown";
                const phone =
                  client.phone ||
                  client.mobile ||
                  client.phoneNumber ||
                  client.mobile_number ||
                  client.contact_number ||
                  client.number ||
                  client.tel ||
                  "";
                const label = phone ? `${name} (${phone})` : name;

                return (
                  <CustomCheckbox
                    key={clientVal}
                    label={label}
                    checked={selectedClient.some(
                      (s) => String(s) === String(clientVal),
                    )}
                    onChange={async (
                      e: React.ChangeEvent<HTMLInputElement>,
                    ) => {
                      if (e.target.checked) {
                        setSelectedClient((prev) =>
                          prev.some((id) => String(id) === clientValStr)
                            ? prev
                            : [...prev, clientVal],
                        );
                      } else {
                        const wasAssigned =
                          assignedClientIds.includes(clientValStr);
                        if (wasAssigned && currentCommunityId) {
                          try {
                            await deleteMember(
                              String(currentCommunityId),
                              client.organization_user?.id || client.id,
                            );

                            setSelectedClient((prev) =>
                              prev.filter((id) => String(id) !== clientValStr),
                            );
                            setAssignedClients((prev) =>
                              prev.filter(
                                (c) =>
                                  String(c.organization_user?.id || c.id) !==
                                  String(
                                    client.organization_user?.id || client.id,
                                  ),
                              ),
                            );

                            setAssignedClientIds((prev) =>
                              prev.filter((id) => id !== clientValStr),
                            );
                            toast.success("Client removed from community", {
                              position: "top-right",
                            });
                            refreshCommunityCounts();
                          } catch (err) {
                            console.error(
                              "Error removing client from community:",
                              err,
                            );
                            showErrorToast(
                              getErrorMessage(
                                err,
                                "Failed to remove client from community.",
                              )
                            );
                          }
                        } else {
                          setSelectedClient((prev) =>
                            prev.filter((id) => String(id) !== clientValStr),
                          );
                        }
                      }
                    }}
                  />
                );
              })}
              {!loadingClients && clients.length === 0 && (
                <Typography sx={{ color: "text.secondary", py: 1 }}>
                  No clients found.
                </Typography>
              )}
              {(hasMoreClients || loadingClients) && (
                <CustomButton
                  disabled={loadingClients}
                  onClick={() =>
                    loadClientsPage(clientsPage + 1, debouncedClientSearch)
                  }
                >
                  {loadingClients
                    ? "Loading…"
                    : `Load more (${clientsTotal - clients.length} left)`}
                </CustomButton>
              )}
            </Box>
          </Box>
        </CustomDialog>
        <CustomDialog
          open={viewDialogOpen}
          onClose={() => setViewDialogOpen(false)}
          title={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography>Assigned Clients</Typography>
              <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
                ({assignedClients.length})
              </Typography>
            </Box>
          }
          actions={
            <CustomButton onClick={() => setViewDialogOpen(false)}>
              Close
            </CustomButton>
          }
        >
          <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
            {assignedClients.length > 0 ? (
              assignedClients.map((client) => (
                <Box
                  key={client.organization_user?.id || client.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography>
                    {client.full_name ||
                      client.organization_user?.full_name ||
                      client.user?.email ||
                      client.organization_user?.email ||
                      "Unknown"}
                  </Typography>
                  <IconButton
                    onClick={async () => {
                      if (currentCommunityId) {
                        try {
                          await deleteMember(
                            String(currentCommunityId),
                            client.organization_user?.id || client.id,
                          );

                          setAssignedClients((prev) =>
                            prev.filter(
                              (c) => c.client_id !== client.client_id,
                            ),
                          );
                          toast.success("Client removed successfully!", {
                            position: "top-right",
                          });
                          // Refresh member counts immediately after removal
                      refreshCommunityCounts();
                    } catch (error) {
                      console.error("Error removing client:", error);
                      showErrorToast(
                        getErrorMessage(error, "Failed to remove client.")
                      );
                    }
                  }
                }}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))
            ) : (
              <Typography>No assigned clients.</Typography>
            )}
          </Box>
        </CustomDialog>
      </Box>
    </Box>
  );
};

export default Community;
